import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    IconTrash,
    IconPlus,
    IconSearch,
    IconArrowLeft,
    IconBuilding,
    IconUsers,
    IconAlertTriangle,
    IconUserPlus,
    IconEye,
    IconEyeOff,
    IconShieldCheck,
    IconPencil,
} from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import OfficeShopSection from '../../../components/office-space/OfficeShopSection';
import { ProfileCompletionCard } from '../../../components/common/ProfileCompletionBanner';
import OtpBoxes from '../../../components/common/OtpBoxes';
import OperatorWageDialog from '../../../components/office-space/OperatorWageDialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../../components/ui/tooltip';
import { sanitizeText } from '../../../utils/sanitize';

// Valid Bangladeshi mobile number: 01 + operator digit (3-9) + 8 digits (11 total).
const BD_PHONE_REGEX = /^01[3-9]\d{8}$/;
const OPERATOR_NAME_MAX = 80;
const OPERATOR_PASSWORD_MAX = 100;

export default function OfficeSpaceSettings() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuthStore();

    const {
        officeSpaces,
        deleteOfficeSpace,
        fetchOfficeSpaces,
        searchUsersByPhone,
        addOperator,
        sendOperatorCreateOtp,
        createOperator,
        removeOperator,
        isLoading,
    } = useOfficeSpaceStore();

    const [officeSpace, setOfficeSpace] = useState(null);
    const isOwnerView = !!(officeSpace && user && officeSpace.ownerId === user.id);

    // Operator management
    const [addOperatorModalOpened, setAddOperatorModalOpened] = useState(false);
    const [phoneSearch, setPhoneSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    // Add-operator modal: two tabs — find an existing user, or create a new one (form → OTP).
    const [modalTab, setModalTab] = useState('search'); // 'search' | 'create'
    const [createStep, setCreateStep] = useState('form'); // 'form' | 'otp'
    const [createName, setCreateName] = useState('');
    const [createPhone, setCreatePhone] = useState('');
    const [createPassword, setCreatePassword] = useState('');
    const [showCreatePassword, setShowCreatePassword] = useState(false);
    const [createOtp, setCreateOtp] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [creatingOperator, setCreatingOperator] = useState(false);
    const [resendSeconds, setResendSeconds] = useState(0);
    // Operator wage dialog (owner only) — holds the OfficeSpaceOperator row being edited
    const [wageOperator, setWageOperator] = useState(null);
    const [removeOperatorModalOpened, setRemoveOperatorModalOpened] = useState(false);
    const [operatorToRemove, setOperatorToRemove] = useState(null);

    // Delete office space management
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadOfficeSpace();
    }, [id, officeSpaces]);

    const loadOfficeSpace = () => {
        const space = officeSpaces.find((os) => os.id === parseInt(id));
        if (space) {
            setOfficeSpace(space);
        } else {
            // Fetch if not in store
            fetchOfficeSpaces();
        }
    };

    const handleSearchUsers = async () => {
        const trimmedPhone = phoneSearch.trim();

        if (!trimmedPhone) {
            notifications.show({
                title: t('common.error'),
                message: t('officeSpace.enterPhoneNumber'),
                color: 'red',
            });
            return;
        }

        // Require full phone number (minimum 11 digits) for privacy/security
        if (trimmedPhone.length < 11) {
            notifications.show({
                title: t('common.error'),
                message: t('officeSpace.enterCompletePhoneNumber'),
                color: 'red',
            });
            return;
        }

        try {
            setSearching(true);
            const results = await searchUsersByPhone(trimmedPhone);

            // Filter out users who are already operators or the owner
            const existingOperatorIds = new Set(
                officeSpace?.operators?.map((op) => op.operatorId) || []
            );
            const filteredResults = results.filter(
                (user) => user.id !== officeSpace.ownerId && !existingOperatorIds.has(user.id)
            );

            setSearchResults(filteredResults);

            if (filteredResults.length === 0) {
                notifications.show({
                    title: t('common.info'),
                    message: t('officeSpace.noUsersFound'),
                    color: 'blue',
                });
            }
        } catch (error) {
            console.error('Search error:', error);
            notifications.show({
                title: t('common.error'),
                message: error.message || t('officeSpace.searchFailed'),
                color: 'red',
            });
        } finally {
            setSearching(false);
        }
    };

    const handleAddOperator = async (userId) => {
        try {
            await addOperator(parseInt(id), userId);

            notifications.show({
                title: t('common.success'),
                message: t('officeSpace.operatorAdded'),
                color: 'green',
            });

            // Close modal and reset search
            setAddOperatorModalOpened(false);
            setPhoneSearch('');
            setSearchResults([]);
        } catch (error) {
            console.error('Add operator error:', error);
            notifications.show({
                title: t('common.error'),
                message: error.message || t('officeSpace.failedToAddOperator'),
                color: 'red',
            });
        }
    };

    // Resend countdown for the verification code.
    useEffect(() => {
        if (resendSeconds <= 0) return undefined;
        const timer = setTimeout(() => setResendSeconds((n) => n - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendSeconds]);

    const resetCreateFlow = () => {
        setCreateStep('form');
        setCreateName('');
        setCreatePhone('');
        setCreatePassword('');
        setShowCreatePassword(false);
        setCreateOtp('');
        setResendSeconds(0);
    };

    const closeAddOperatorModal = () => {
        setAddOperatorModalOpened(false);
        setPhoneSearch('');
        setSearchResults([]);
        setModalTab('search');
        resetCreateFlow();
    };

    const openCreateTab = () => {
        // Carry over what the owner already typed in the search box.
        if (!createPhone) setCreatePhone(phoneSearch.replace(/\D/g, '').slice(0, 11));
        setModalTab('create');
    };

    const showError = (message) => notifications.show({ title: t('common.error'), message, color: 'red' });

    // Returns the cleaned values, or null after showing why the form isn't valid yet.
    const validateCreateForm = () => {
        const name = sanitizeText(createName, { maxLen: OPERATOR_NAME_MAX }).trim();
        if (!name) { showError(t('officeSpace.enterValidOperatorName')); return null; }
        if (!BD_PHONE_REGEX.test(createPhone)) { showError(t('officeSpace.enterValidBdPhone')); return null; }
        if (createPassword.length < 6) { showError(t('officeSpace.passwordTooShort')); return null; }
        return { name, phone: createPhone, password: createPassword };
    };

    const handleSendCode = async () => {
        if (sendingCode) return;
        const values = validateCreateForm();
        if (!values) return;
        try {
            setSendingCode(true);
            await sendOperatorCreateOtp(parseInt(id), values.phone);
            setCreateOtp('');
            setCreateStep('otp');
            setResendSeconds(60);
            notifications.show({ title: t('common.success'), message: t('officeSpace.codeSent'), color: 'green' });
        } catch (error) {
            if (error?.code === 'USER_ALREADY_EXISTS') {
                showError(t('officeSpace.phoneAlreadyRegistered'));
            } else if (error?.code === 'OTP_RESEND_COOLDOWN') {
                // A code went out moments ago — move on to entering it.
                setCreateOtp('');
                setCreateStep('otp');
                setResendSeconds(Number(error?.data?.seconds) || 60);
            } else {
                showError(error.message || t('officeSpace.failedToSendCode'));
            }
        } finally {
            setSendingCode(false);
        }
    };

    const handleCreateOperator = async () => {
        if (creatingOperator) return;
        const values = validateCreateForm();
        if (!values) return;
        if (!/^\d{6}$/.test(createOtp)) { showError(t('officeSpace.enterSixDigitCode')); return; }
        try {
            setCreatingOperator(true);
            await createOperator(parseInt(id), { ...values, otp: createOtp });
            notifications.show({ title: t('common.success'), message: t('officeSpace.operatorCreated'), color: 'green' });
            closeAddOperatorModal();
        } catch (error) {
            showError(error.message || t('officeSpace.failedToCreateOperator'));
        } finally {
            setCreatingOperator(false);
        }
    };

    // Short label for how an operator is paid, shown on their row.
    const wageSummary = (op) => {
        if (!op.wageType || op.wageAmount == null) return t('operatorPay.notSet');
        const amount = Number(op.wageAmount).toLocaleString('en-BD', { maximumFractionDigits: 2 });
        if (op.wageType === 'percent') return t('operatorPay.percentOfIncome', { pct: amount });
        const key = op.wagePeriod === 'week' ? 'perWeek' : op.wagePeriod === 'month' ? 'perMonth' : 'perDay';
        return t(`operatorPay.${key}`, { amount });
    };

    const confirmRemoveOperator = (operator) => {
        setOperatorToRemove(operator);
        setRemoveOperatorModalOpened(true);
    };

    const handleRemoveOperator = async () => {
        if (!operatorToRemove) return;

        try {
            await removeOperator(parseInt(id), operatorToRemove.operatorId);

            notifications.show({
                title: t('common.success'),
                message: t('officeSpace.operatorRemoved'),
                color: 'green',
            });

            setRemoveOperatorModalOpened(false);
            setOperatorToRemove(null);
        } catch (error) {
            console.error('Remove operator error:', error);
            notifications.show({
                title: t('common.error'),
                message: error.message || t('officeSpace.failedToRemoveOperator'),
                color: 'red',
            });
        }
    };

    const handleDeleteOfficeSpace = async () => {
        if (deleteConfirmText !== 'DELETE') {
            return;
        }

        try {
            setDeleting(true);

            await deleteOfficeSpace(parseInt(id));

            notifications.show({
                title: t('common.success'),
                message: t('officeSpace.officeSpaceDeleted'),
                color: 'green',
            });

            // Navigate to dashboard after successful deletion
            navigate('/');
        } catch (error) {
            console.error('Delete office space error:', error);
            notifications.show({
                title: t('common.error'),
                message: error.message || t('officeSpace.failedToDeleteOfficeSpace'),
                color: 'red',
            });
            setDeleting(false);
        }
    };

    if (!officeSpace) {
        return (
            <div className="w-full font-hind py-16">
                <div className="flex items-center justify-center">
                    <span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    const isOwner = user?.id === officeSpace.ownerId;

    // The completion bonus counts ONLY the first/default OWNED office's shop info —
    // mirror the backend pick (isDefault first, then lowest id) so the nudge shows
    // only on the office whose fields actually move the completion percent.
    const ownedSpaces = officeSpaces.filter((os) => os.ownerId === user?.id);
    const bonusOffice = ownedSpaces.find((os) => os.isDefault)
        || ownedSpaces.reduce((min, os) => (!min || os.id < min.id ? os : min), null);
    const isBonusOffice = bonusOffice?.id === officeSpace.id;

    return (
        <div className="w-full font-hind space-y-5 sm:space-y-6 py-2 sm:py-4">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/officespaces')}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                    aria-label={t('common.back')}
                >
                    <IconArrowLeft size={18} stroke={1.8} />
                </button>
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <IconBuilding size={20} stroke={1.7} />
                </span>
                <div className="min-w-0">
                    <h1 className="truncate text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                        {officeSpace.name}
                    </h1>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t('officeSpace.workspaceSettings')}
                    </p>
                </div>
            </div>

            {/* Profile-completion card — the bonus counts only the FIRST/default owned
                office's shop profile, so the full card (with the exact missing fields)
                shows only on that office's settings page. Self-hides at 100%. */}
            {isOwner && isBonusOffice && <ProfileCompletionCard />}

            <div className="flex w-full flex-col gap-4">
                {/* Operator Management */}
                {isOwner && (
                    <section className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4 sm:p-5 shadow-sm">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <IconUsers size={18} stroke={1.7} className="text-zinc-400" />
                                    <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                                        {t('officeSpace.operators')}
                                    </h2>
                                    <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-xs font-semibold text-zinc-500 dark:text-zinc-400">
                                        {officeSpace.operators?.length || 0}
                                    </span>
                                </div>
                                <button
                                    onClick={() => setAddOperatorModalOpened(true)}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 px-3.5 py-2 text-[13px] font-semibold text-white transition-colors"
                                >
                                    <IconPlus size={15} stroke={1.8} />
                                    {t('officeSpace.addOperator')}
                                </button>
                            </div>

                            {officeSpace.operators && officeSpace.operators.length > 0 ? (
                                <div className="flex flex-col gap-2">
                                    {officeSpace.operators.map((operator) => (
                                        <div
                                            key={operator.id}
                                            className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3.5"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary/10 text-[13px] font-semibold text-primary">
                                                    {operator.operator.name?.charAt(0)?.toUpperCase() || '?'}
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                                                        {operator.operator.name}
                                                    </p>
                                                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                                        {operator.operator.phone}
                                                        <span className="mx-1.5">·</span>
                                                        {t('officeSpace.addedOn')} {new Date(operator.addedAt).toLocaleDateString()}
                                                    </p>
                                                    {isOwnerView && (
                                                        <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                                            operator.wageType
                                                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                                                : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'
                                                        }`}>
                                                            {wageSummary(operator)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-none items-center gap-1">
                                            {isOwnerView && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            onClick={() => setWageOperator(operator)}
                                                            aria-label={t('operatorPay.editWage')}
                                                            className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-zinc-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                                        >
                                                            <IconPencil size={16} stroke={1.7} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top" className="max-w-[240px] text-center leading-snug">
                                                        {t('operatorPay.editWageTip')}
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                            <button
                                                onClick={() => confirmRemoveOperator(operator)}
                                                title={t('officeSpace.removeOperator')}
                                                className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            >
                                                <IconTrash size={16} stroke={1.7} />
                                            </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-zinc-700 py-8 text-center">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-400">
                                        <IconUserPlus size={22} stroke={1.6} />
                                    </span>
                                    <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
                                        {t('officeSpace.noOperators')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* Shop / Business (Dokan) — name, status and the office's business profile,
                    all in one form (the office name IS the shop name). */}
                {isOwner && <OfficeShopSection officeSpace={officeSpace} isOwner={isOwner} />}

                {/* Danger Zone */}
                {isOwner && !officeSpace.isDefault && (
                    <section className="rounded-2xl border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-900/10 p-4 sm:p-5 shadow-sm">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-2">
                                <IconAlertTriangle size={18} stroke={1.7} className="text-red-500" />
                                <h2 className="text-base font-semibold text-red-600 dark:text-red-400">
                                    {t('officeSpace.dangerZone')}
                                </h2>
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                                        {t('officeSpace.deleteOfficeSpace')}
                                    </p>
                                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                        {t('officeSpace.deleteOfficeSpaceDescription')}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setDeleteModalOpened(true)}
                                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 px-4 py-2 text-[13px] font-semibold text-white transition-colors flex-shrink-0"
                                >
                                    <IconTrash size={15} stroke={1.8} />
                                    {t('common.delete')}
                                </button>
                            </div>
                        </div>
                    </section>
                )}
            </div>

            <OperatorWageDialog
                open={!!wageOperator}
                onClose={() => setWageOperator(null)}
                officeSpaceId={officeSpace?.id}
                link={wageOperator}
            />

            {/* Add Operator Modal — search an existing user, or create a new operator (OTP-verified) */}
            <Dialog
                open={addOperatorModalOpened}
                onOpenChange={(v) => { if (!v) closeAddOperatorModal(); }}
            >
                {/* Fixed minimum height: the tabs, results list, form and OTP step all swap inside
                    the same box, so the modal never jumps in size as the content changes. */}
                <DialogContent className="max-w-2xl w-[calc(100vw-24px)] p-6 sm:p-8 min-h-[500px] max-h-[90vh] overflow-y-auto flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-xl">{t('officeSpace.addOperator')}</DialogTitle>
                    </DialogHeader>

                    {/* Mode switch — one clear choice instead of two actions side by side */}
                    <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-zinc-100 dark:bg-zinc-800 p-1">
                        {[
                            { id: 'search', label: t('officeSpace.tabSearchExisting'), Icon: IconSearch, onClick: () => setModalTab('search') },
                            { id: 'create', label: t('officeSpace.tabCreateNew'), Icon: IconUserPlus, onClick: openCreateTab },
                        ].map(({ id: tabId, label, Icon, onClick }) => (
                            <button
                                key={tabId}
                                type="button"
                                onClick={onClick}
                                className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-semibold transition-colors ${
                                    modalTab === tabId
                                        ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm'
                                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                                }`}
                            >
                                <Icon size={16} stroke={1.9} />
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* ── Search view ── */}
                    {modalTab === 'search' && (
                        <div className="flex flex-col gap-5 flex-1">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                    {t('officeSpace.searchByPhone')}
                                </label>
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                    {t('officeSpace.fullPhoneNumberRequired')}
                                </p>
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="relative flex-1 min-w-[200px]">
                                        <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                        <input
                                            type="text"
                                            inputMode="tel"
                                            maxLength={20}
                                            value={phoneSearch}
                                            onChange={(e) => setPhoneSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                                            placeholder={t('officeSpace.fullPhoneNumberPlaceholder')}
                                            className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                                        />
                                    </div>
                                    <button
                                        onClick={handleSearchUsers}
                                        disabled={searching}
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors disabled:opacity-60"
                                    >
                                        {searching && (
                                            <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                        )}
                                        {t('common.search')}
                                    </button>
                                </div>
                            </div>

                            {searchResults.length === 0 && (
                                <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-700 px-6 py-8 text-center">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                                        <IconSearch size={20} />
                                    </span>
                                    <p className="max-w-sm text-sm text-zinc-500 dark:text-zinc-400">{t('officeSpace.searchEmptyHint')}</p>
                                    <button type="button" onClick={openCreateTab} className="text-[13px] font-semibold text-primary hover:underline">
                                        {t('officeSpace.tabCreateNew')} →
                                    </button>
                                </div>
                            )}

                            {searchResults.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                                    {t('officeSpace.searchResults')}
                                </p>
                                {searchResults.map((user) => (
                                    <div
                                        key={user.id}
                                        className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3.5"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary/10 text-[13px] font-semibold text-primary">
                                                {user.name?.charAt(0)?.toUpperCase() || '?'}
                                            </span>
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                                                    {user.name}
                                                </p>
                                                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                                    {user.phone}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleAddOperator(user.id)}
                                            className="inline-flex flex-none items-center gap-1 rounded-lg bg-primary hover:bg-primary/90 px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                                        >
                                            <IconPlus size={14} stroke={1.9} />
                                            {t('common.add')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        </div>
                    )}

                    {/* ── Create view: details → verification code ── */}
                    {modalTab === 'create' && (
                        <div className="flex flex-col gap-5 flex-1">
                            {createStep === 'form' ? (
                                <>
                                    <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('officeSpace.createOperatorHint')}</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('officeSpace.operatorName')}</label>
                                            <input
                                                type="text"
                                                value={createName}
                                                maxLength={OPERATOR_NAME_MAX}
                                                onChange={(e) => setCreateName(e.target.value)}
                                                placeholder={t('officeSpace.operatorNamePlaceholder')}
                                                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-2.5 px-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('officeSpace.operatorPhone')}</label>
                                            <input
                                                type="tel"
                                                inputMode="numeric"
                                                value={createPhone}
                                                maxLength={11}
                                                onChange={(e) => setCreatePhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                                placeholder="01XXXXXXXXX"
                                                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-2.5 px-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1.5 sm:col-span-2">
                                            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('officeSpace.operatorPassword')}</label>
                                            <div className="relative">
                                                <input
                                                    type={showCreatePassword ? 'text' : 'password'}
                                                    autoComplete="new-password"
                                                    value={createPassword}
                                                    maxLength={OPERATOR_PASSWORD_MAX}
                                                    onChange={(e) => setCreatePassword(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                                                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-2.5 pl-3 pr-10 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCreatePassword((v) => !v)}
                                                    aria-label={showCreatePassword ? t('officeSpace.hidePassword') : t('officeSpace.showPassword')}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                                                >
                                                    {showCreatePassword ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                                                </button>
                                            </div>
                                            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('officeSpace.operatorPasswordHint')}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 pt-1 mt-auto">
                                        <button
                                            type="button"
                                            onClick={handleSendCode}
                                            disabled={sendingCode}
                                            className="inline-flex items-center gap-2 rounded-lg bg-primary hover:bg-primary/90 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors disabled:opacity-60"
                                        >
                                            {sendingCode
                                                ? <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                                : <IconShieldCheck size={16} />}
                                            {t('officeSpace.sendCode')}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-zinc-600 dark:text-zinc-300">
                                        {t('officeSpace.codeSentTo', { phone: createPhone })}
                                    </p>
                                    <OtpBoxes value={createOtp} onChange={setCreateOtp} autoFocus />
                                    <div className="flex justify-center">
                                        {resendSeconds > 0 ? (
                                            <span className="text-xs text-zinc-500 dark:text-zinc-400">{t('officeSpace.resendIn', { seconds: resendSeconds })}</span>
                                        ) : (
                                            <button type="button" onClick={handleSendCode} disabled={sendingCode} className="text-xs font-semibold text-primary hover:underline disabled:opacity-60">
                                                {t('officeSpace.resendCode')}
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1 mt-auto">
                                        <button
                                            type="button"
                                            onClick={() => { setCreateStep('form'); setCreateOtp(''); }}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 px-4 py-2.5 text-[13px] font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                                        >
                                            <IconArrowLeft size={15} />
                                            {t('officeSpace.changeDetails')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCreateOperator}
                                            disabled={creatingOperator || createOtp.length !== 6}
                                            className="inline-flex items-center gap-2 rounded-lg bg-primary hover:bg-primary/90 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors disabled:opacity-60"
                                        >
                                            {creatingOperator
                                                ? <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                                : <IconUserPlus size={16} />}
                                            {t('officeSpace.verifyAndCreate')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Remove Operator Confirmation Modal */}
            <Dialog
                open={removeOperatorModalOpened}
                onOpenChange={(v) => {
                    if (!v) {
                        setRemoveOperatorModalOpened(false);
                        setOperatorToRemove(null);
                    }
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('officeSpace.removeOperator')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                            {t('officeSpace.confirmRemoveOperator', {
                                name: operatorToRemove?.operator?.name,
                            })}
                        </p>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                onClick={() => {
                                    setRemoveOperatorModalOpened(false);
                                    setOperatorToRemove(null);
                                }}
                                className="rounded-lg border border-zinc-300 dark:border-zinc-600 px-4 py-2 text-[13px] font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleRemoveOperator}
                                disabled={isLoading}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 px-4 py-2 text-[13px] font-semibold text-white transition-colors disabled:opacity-50"
                            >
                                {isLoading && (
                                    <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                )}
                                {t('common.remove')}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete OfficeSpace Confirmation Modal */}
            <Dialog
                open={deleteModalOpened}
                onOpenChange={(v) => {
                    if (!v) {
                        setDeleteModalOpened(false);
                        setDeleteConfirmText('');
                    }
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('officeSpace.confirmDeleteOfficeSpace')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                        <div className="flex items-start gap-2.5 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/70 dark:bg-red-900/10 p-3">
                            <IconAlertTriangle size={18} stroke={1.7} className="mt-0.5 flex-none text-red-500" />
                            <p className="text-sm font-medium text-red-600 dark:text-red-400">
                                {t('officeSpace.deleteOfficeSpaceWarning')}
                            </p>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                {t('officeSpace.typeDELETE')}
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder={t('officeSpace.typeDeleteToConfirm')}
                                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            {deleteConfirmText && deleteConfirmText !== 'DELETE' && (
                                <p className="text-xs text-red-500">Please type DELETE exactly</p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-2">
                            <button
                                onClick={() => {
                                    setDeleteModalOpened(false);
                                    setDeleteConfirmText('');
                                }}
                                className="rounded-lg border border-zinc-300 dark:border-zinc-600 px-4 py-2 text-[13px] font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteOfficeSpace}
                                disabled={deleting || deleteConfirmText !== 'DELETE'}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 px-4 py-2 text-[13px] font-semibold text-white transition-colors disabled:opacity-50"
                            >
                                {deleting && (
                                    <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                )}
                                {t('common.delete')}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
